<template>
  <div class="object-select-and-file-input clearfix">
    <div class="form-group row" :class="{ 'has-feedback' : isDirty }">
      <label
        :for="options.name"
        :class="options.fullWidth ? 'col-sm-12' : 'col-sm-3 col-form-label'">
        {{ options.label }}
        <span v-if="required" class="required">*</span>
      </label>
      <div :class="options.fullWidth ? 'col-sm-12' : 'col-sm-9'">
        <div class="row">
          <div class="col-sm-8 col-md-10 pr-sm-0">
            <object-select-input :options="{ noLabel: true, ...options }" v-model="value.object" @input="validate($event)"></object-select-input>
          </div>
          <div class="col-sm-4 col-md-2 mt-sm-0 mt-1 pl-sm-0">
            <div class="upload-btn-wrapper" ref="dropzone">
              <button class="btn btn-info upload-button" type="button">Choose file...</button>
              <input type="hidden" :name="options.name" :id="options.name + '-url'" :value="value.file"/>
            </div>
          </div>
        </div>
        <div v-if="hasError" class="alert alert-danger my-0">
          {{ hasError }}
        </div>
        <div :class="validationClass" v-show="validationMessage">
          {{ validationMessage }}
          <br/>
        </div>
        <span v-if="this.fileName && !this.isUploading" class="feedback">
          File uploaded: {{ this.fileName }} 
          - <a :href="'https://roscon.s3.amazonaws.com/' + value.file.key" target="_blank">Preview</a>
          | <span class="interactive-cursor text-danger" @click.prevent="validate({})"><i class="fa fa-times"></i> Remove</span>
        </span>
        <span v-else class="feedback">
          No file selected
        </span>
        <div class="progress" style="height: 20px;" v-show="isUploading">
          <div class="progress-bar progress-bar-striped progress-bar-animated" role="progressbar" :style="{ width: progress + '%' }">
            {{ progress }}%
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
  import _ from 'lodash'
  import ObjectSelectInput from './ObjectSelectInput'
  import {
    validationClass,
    formControlValidationClass,
    validationMessage
  } from '@/util/helpers'

  export default {
    name: 'ObjectSelectAndFileInput',
    components: {
      ObjectSelectInput
    },
    props: {
      value: {
        type: Object
      },
      options: {
        type: Object,
        default () {
          return {
            value: ''
          }
        }
      }
    },
    computed: {
      height () {
        return this.options.height || 'auto'
      },
      width () {
        return this.options.width || 'auto'
      },
      required () {
        return this.options.textRequired && this.options.fileRequired
      },
      validationClass,
      formControlValidationClass,
      validationMessage,
      invalidMessage () {
        if (this.required) {
          return this.options.label + ' text and file are required'
        } else if (this.options.textRequired) {
          return this.options.label + ' text is required'
        } else if (this.options.fileRequired) {
          return this.options.label + ' file is required'
        }
      }
    },
    data () {
      return {
        isDirty: false,
        isValid: false,
        fileName: '',
        isUploading: false,
        progress: 0,
        hasError: '',
        fileTypes: {
          image: /^(image\/(gif|jpe?g|png))$/,
          pdf: /^(application\/(pdf|x-pdf|acrobat))|(text\/(pdf|x-pdf))|(applications\/vnd\.pdf)$/
        },
        validFileType: /^(image\/(gif|jpe?g|png))|(application\/(pdf|x-pdf|acrobat))|(text\/(pdf|x-pdf))|(applications\/vnd\.pdf)$/,
        invalidFileTypeError: 'Not an accepted file type. Accepted file types: JPG, PNG, GIF and PDF'
      }
    },
    mounted () {
      if (this.options.validFileType === 'image') {
        this.validFileType = this.fileTypes.image
        this.invalidFileTypeError = 'You must upload an image file. Accepted file types: JPG, PNG and GIF'
      } else if (this.options.validFileType === 'pdf') {
        this.validFileType = this.fileTypes.pdf
        this.invalidFileTypeError = 'You must upload a PDF file.'
      }
      if (this.value.file.fileName) {
        this.fileName = this.value.file.fileName
      }
      window.fd.logging = false
      let zone = new window.FileDrop(this.$refs.dropzone, {
        iframe: {
          url: `/api/admin/media-uploader?height=${this.height}&width=${this.width}`,
          fileParam: 'media',
          callbackParam: 'iframeCallback'
        }
      })
      let $vm = this
      zone.event('send', function (files) {
        $vm.isUploading = true
        files.each(function (file) {
          $vm.fileName = file.name
          $vm.hasError = false
          let uploadErrors = []
          let acceptFileTypes = $vm.validFileType
          if (file.type.length && !acceptFileTypes.test(file.type)) {
            uploadErrors.push($vm.invalidFileTypeError)
          }
          if (file.size.length && file.size > 10000000) {
            uploadErrors.push('Filesize is too big')
          }
          if (uploadErrors.length > 0) {
            $vm.progress = 0
            $vm.isUploading = false
            $vm.hasError = uploadErrors.join('\n')
          } else {
            file.event('progress', function (sent, total, xhr, event) {
              $vm.progress = parseInt(sent / total * 100, 10)
            })
            file.event('done', function (xhr) {
              $vm.progress = 0
              $vm.isUploading = false
              let json = JSON.parse(xhr.responseText)
              $vm.validate({
                ...$vm.value,
                file: {
                  key: json.key,
                  fileName: $vm.fileName,
                  fileType: json.ext
                }
              })
            })
            file.event('error', function (event, xhr) {
              $vm.progress = 0
              $vm.isUploading = false
              uploadErrors.push(xhr.responseText)
              $vm.hasError = uploadErrors.join('\n')
            })
            file.sendTo(`/api/admin/media-uploader?height=${$vm.height}&width=${$vm.width}`, {
              extraHeaders: true,
              method: 'POST'
            })
          }
        })
      })
      zone.event('iframeDone', function (json) {
        $vm.progress = 0
        $vm.isUploading = false
        $vm.validate({
          ...$vm.value,
          file: {
            key: json.key,
            fileName: $vm.fileName,
            fileType: json.ext
          }
        })
      })
      window.fd.addEvent(window.fd.byID('multiple'), 'change', function (e) {
        zone.multiple(e.currentTarget || e.srcElement.checked)
      })
    },
    methods: {
      validate (val = this.value) {
        this.isDirty = true
        this.isValid = true
        if (_.isEmpty(val)) {
          this.fileName = ''
        }
        if (_.isString(val) || val === null) {
          val = {
            ...this.value,
            object: val
          }
        }
        this.$emit('input', val)
        if (this.options.fileRequired) {
          this.isValid = Boolean(this.value.file.key)
        } else {
          this.isValid = true
        }
        if (this.isValid) {
          if (this.options.textRequired) {
            this.isValid = Boolean(this.value.text)
          } else {
            this.isValid = true
          }
        }
      }
    }
  }
</script>



// WEBPACK FOOTER //
// src/components/inputs/ObjectSelectAndFileInput.vue